import { useEffect, useState } from "react";
import AgoraRTC, {
  useJoin,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  usePublish,
  useRemoteUsers,
  useRemoteAudioTracks,
  useClientEvent,
  useRTCClient,
} from "agora-rtc-react";
import { getGeneratedAgoraToken } from "@/api/token";
import toast from "react-hot-toast";

const appid = import.meta.env.VITE_AGORA_APP_ID;
const uid = Math.floor(Math.random() * 10_000_000);

export const useInstantCall = (mode, channelName) => {
  console.log(`[InstantCall] Initializing instant call hook for mode: ${mode}, channel: ${channelName}`);
  
  // Early validation
  if (!mode || (mode !== "video" && mode !== "voice")) {
    console.error(`[InstantCall] Invalid mode provided: ${mode}`);
    throw new Error(`Invalid call mode: ${mode}`);
  }
  
  if (!channelName) {
    console.error(`[InstantCall] No channel name provided`);
    throw new Error(`Channel name is required`);
  }
  
  const [isPageReady, setIsPageReady] = useState(false);
  const [isUserReady, setIsUserReady] = useState(false);
  const [token, setToken] = useState(null);
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(mode === "video");
  const [isFrontFacing, setIsFrontFacing] = useState(true);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [microphoneDevices, setMicrophoneDevices] = useState([]);
  const [speakerDevices, setSpeakerDevices] = useState([]);
  const [disconnectedUsers, setDisconnectedUsers] = useState([]);
  const client = useRTCClient();

  // Use channel from props
  const channel = channelName || "test-channel"; // Fallback for testing

  // Join the channel
  const { isConnected, isLoading, error } = useJoin(
    { channel, token, uid, appid },
    isPageReady && isUserReady
  );
  
  // Log connection state changes
  useEffect(() => {
    console.log(`[InstantCall] Connection state - isConnected: ${isConnected}, isLoading: ${isLoading}, hasError: ${!!error}`);
    if (error) {
      console.error(`[InstantCall] Join error:`, error);
    }
    if (isConnected) {
      console.log(`[InstantCall] Successfully joined channel: ${channel} with uid: ${uid}`);
    }
  }, [isConnected, isLoading, error]);

  // Get microphone track (enabled by default)
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);

  // Get camera track (conditional on mode)
  const { localCameraTrack } = useLocalCameraTrack(
    mode === "video" ? cameraOn : false,
    { facingMode: "user" }
  );

  console.log(`[InstantCall] Tracks initialized - mode: ${mode}, micTrack:`, !!localMicrophoneTrack, 'cameraTrack:', !!localCameraTrack);

  // Publish tracks
  usePublish(
    mode === "video" ? [localMicrophoneTrack, localCameraTrack] : [localMicrophoneTrack], 
    isPageReady && isUserReady
  );

  const remoteUsers = useRemoteUsers();
  
  // Log remote users changes
  useEffect(() => {
    console.log(`[InstantCall] Remote users count: ${remoteUsers.length}`);
    remoteUsers.forEach((user, index) => {
      console.log(`[InstantCall] Remote user ${index}: uid=${user.uid}, hasVideo=${user.hasVideo}, hasAudio=${user.hasAudio}`);
    });
  }, [remoteUsers]);
  
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);

  useClientEvent(client, 'user-joined', (user) => {
    console.log(`[InstantCall] User joined: ${user.uid}`, user);
  });
  
  useClientEvent(client, 'user-left', (user, reason) => {
    console.log(`[InstantCall] User left: ${user.uid}, reason: ${reason}`);
    if (reason === 'ServerTimeOut') {
      toast.error(`User disconnected abruptly due to network issues`);
      setDisconnectedUsers(prev => [...prev, { uid: user.uid, reason, timestamp: Date.now() }]);
    } else if (reason === 'Quit') {
      toast(`The user left the call`);
    }
  });
  
  useClientEvent(client, 'user-published', (user, mediaType) => {
    console.log(`[InstantCall] User ${user.uid} published ${mediaType}`);
  });
  
  useClientEvent(client, 'user-unpublished', (user, mediaType) => {
    console.log(`[InstantCall] User ${user.uid} unpublished ${mediaType}`);
  });

  // Safe audio playback with error handling
  useEffect(() => {
    console.log(`[InstantCall] Audio tracks received:`, audioTracks.length);
    audioTracks.forEach((track, index) => {
      try {
        if (track && typeof track.play === 'function') {
          track.play();
          console.log(`[InstantCall] Playing audio track ${index}`);
        } else {
          console.warn(`[InstantCall] Invalid audio track at index ${index}:`, track);
        }
      } catch (error) {
        console.error(`[InstantCall] Failed to play audio track ${index}:`, error);
      }
    });
  }, [audioTracks]);

  useEffect(() => {
    console.log(`[InstantCall] Starting initialization for channel: ${channel}`);
    if (channel) {
      fetchToken();
      // Delay device fetching to avoid conflicts
      setTimeout(() => {
        fetchDevices();
      }, 2000);
    }
  }, [channel]);

  useEffect(() => {
    if (!token) setIsPageReady(false);
    setIsPageReady(true);
  }, [token]);

  const fetchToken = async () => {
    console.log(`[InstantCall] Fetching token for channel: ${channel}, uid: ${uid}`);
    try {
      const token = await getGeneratedAgoraToken(channel, uid);
      if (token) {
        console.log(`[InstantCall] Token received successfully`);
        setToken(token);
      } else {
        console.error(`[InstantCall] No token received`);
      }
    } catch (err) {
      console.error(`[InstantCall] Token fetch failed:`, err);
      toast.error('Failed to get call token. Please try again.');
    }
  };

  const fetchDevices = async () => {
    try {
      const cameras = await AgoraRTC.getCameras(false);
      const microphones = await AgoraRTC.getMicrophones(false);
      const speakers = await AgoraRTC.getPlaybackDevices(false);

      setCameraDevices(cameras);
      setMicrophoneDevices(microphones);
      setSpeakerDevices(speakers);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCameraDeviceChange = async (e) => {
    const deviceId = e.target.value;
    console.log(`[InstantCall] Changing camera device to: ${deviceId}`);
    try {
      if (localCameraTrack && typeof localCameraTrack.setDevice === 'function') {
        await localCameraTrack.setDevice({ deviceId });
        console.log(`[InstantCall] Camera device changed successfully`);
      } else {
        console.warn(`[InstantCall] Camera track not ready or setDevice not available`);
      }
    } catch (error) {
      console.error(`[InstantCall] Failed to change camera device:`, error);
      toast.error('Failed to change camera device');
    }
  };

  const handleMicrophoneDeviceChange = async (e) => {
    const deviceId = e.target.value;
    console.log(`[InstantCall] Changing microphone device to: ${deviceId}`);
    try {
      if (localMicrophoneTrack && typeof localMicrophoneTrack.setDevice === 'function') {
        await localMicrophoneTrack.setDevice(deviceId);
        console.log(`[InstantCall] Microphone device changed successfully`);
      } else {
        console.warn(`[InstantCall] Microphone track not ready or setDevice not available`);
      }
    } catch (error) {
      console.error(`[InstantCall] Failed to change microphone device:`, error);
      toast.error('Failed to change microphone device');
    }
  };

  const handleSpeakerDeviceChange = async (e) => {
    const deviceId = e.target.value;
    console.log(`[InstantCall] Changing speaker device to: ${deviceId}`);
    if (audioTracks && audioTracks.length > 0) {
      audioTracks.forEach(async (track, index) => {
        try {
          if (track && typeof track.setPlaybackDevice === 'function') {
            await track.setPlaybackDevice(deviceId);
            console.log(`[InstantCall] Speaker device changed for track ${index}`);
          } else {
            console.warn(`[InstantCall] Invalid audio track for speaker change at index ${index}`);
          }
        } catch (error) {
          console.warn(`[InstantCall] Failed to set playback device for track ${index}:`, error);
        }
      });
    } else {
      console.log(`[InstantCall] No audio tracks available for speaker change`);
    }
  };

  const handleCameraSwitch = async () => {
    const newFacingMode = isFrontFacing ? "environment" : "user";
    console.log(`[InstantCall] Switching camera to: ${newFacingMode}`);
    try {
      if (localCameraTrack && typeof localCameraTrack.setDevice === 'function') {
        await localCameraTrack.setDevice({
          facingMode: newFacingMode,
        });
        setIsFrontFacing((prev) => !prev);
        console.log(`[InstantCall] Camera switched successfully to: ${newFacingMode}`);
      } else {
        console.warn(`[InstantCall] Camera track not ready or setDevice not available`);
      }
    } catch (error) {
      console.error(`[InstantCall] Failed to switch camera:`, error);
      toast.error('Failed to switch camera');
    }
  };

  return {
    cameraOn,
    localCameraTrack,
    setCamera,
    micOn,
    setMic,
    localMicrophoneTrack,
    remoteUsers,
    setIsUserReady,
    isConnected,
    cameraDevices,
    handleCameraDeviceChange,
    microphoneDevices,
    handleMicrophoneDeviceChange,
    speakerDevices,
    handleSpeakerDeviceChange,
    handleCameraSwitch,
    isUserReady: isUserReady,
    disconnectedUsers,
    isPageReady,
    isLoading: false,
    // Include navigation state for error checking
    channel,
    userSocketId: "test-user",
  };
};